import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaPhone, FaEnvelope, FaDownload, FaCheckCircle } from 'react-icons/fa';
const WEB3FORM_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

const BrochureModal = ({ isOpen, onClose, language = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDownload = () => {
    // Create a download link for the brochure
    const brochureMap = {
      'French': 'THE-LINGUA-Alliance_French.pdf',
      'German': 'THE-LINGUA-Alliance-German.pdf',
      'Spanish': 'THE-LINGUA-Alliance-Spanish.pdf'
    };
    
    const brochureFileName = brochureMap[language] || 'The_Lingua_Alliance_Brochure.pdf';
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/Brochures/${brochureFileName}`;
    link.download = brochureFileName;
    link.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsDownloading(true);
      if (!WEB3FORM_ACCESS_KEY) {
        console.error('WEB3FORM_ACCESS_KEY is not configured');
        setIsDownloading(false);
        alert('Form temporarily unavailable. Please try again later.');
        return;
      }
      
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            access_key: WEB3FORM_ACCESS_KEY,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            language: language || 'General',
            subject: `Brochure Download Request${language ? ' - ' + language : ''}`,
            from_name: 'The Lingua Alliance - Brochure Request'
          })
        });

        const result = await response.json();
        
        if (result.success) {
          console.log('Brochure request submitted successfully:', result);
          
          // Download brochure after successful submission
          setTimeout(() => {
            handleDownload();
            setShowSuccess(true);
            setIsDownloading(false);
            
            // Reset form
            setFormData({
              name: '',
              phone: '',
              email: ''
            });
            setErrors({});
          }, 500);
        } else {
          console.error('Form submission failed:', result);
          setIsDownloading(false);
          alert('Failed to submit form. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setIsDownloading(false);
        alert('An error occurred. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      phone: '',
      email: ''
    });
    setErrors({});
    setShowSuccess(false);
    setIsDownloading(false);
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Success Modal */}
      {showSuccess && (
        <div className="relative z-10 w-full max-w-md mx-4 animate-[slideUp_0.3s_ease-out]">
          <div className="rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.3)]">
            <div className="text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 text-4xl text-white mb-6 animate-[scaleIn_0.5s_ease-out]">
                <FaCheckCircle />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-4">
                Download Started!
              </h3>
              <p className="text-lg text-ink-muted mb-8">
                Your brochure download has started. Thank you for your interest in The Lingua Alliance!
              </p>
              <button
                onClick={handleSuccessClose}
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)]"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {!showSuccess && (
        <div className="relative z-10 w-full max-w-lg mx-4 animate-[slideUp_0.3s_ease-out]">
          <div className="rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.3)]">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-6 right-6 text-ink-muted hover:text-ink transition-colors"
              aria-label="Close modal"
            >
              <FaTimes className="text-2xl" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/10 to-primary-soft/10 text-3xl text-primary mb-4">
                <FaDownload />
              </div>
              <h2 className="text-3xl font-bold text-ink mb-2">
                Download Our Brochure
              </h2>
              <p className="text-ink-muted">
                {language ? `Get detailed information about our ${language} courses` : 'Get detailed information about all our courses'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-ink mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaUser />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className={`w-full rounded-xl border ${errors.name ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-ink mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaPhone />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className={`w-full rounded-xl border ${errors.phone ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-ink mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaEnvelope />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={`w-full rounded-xl border ${errors.email ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isDownloading}
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] mt-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDownloading ? 'Processing...' : (
                  <>
                    <FaDownload className="mr-2" />
                    Download Brochure
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrochureModal;
